import React, { useEffect, useState } from 'react';

import { Card, Icon, IconButton, Typography } from '@material-ui/core';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useSubheader } from '../../../_metronic/layout';
import { submissionCardsFields, submissionFields } from '../../../airtable/metadata/submissions';
import { fetchCoupons, fetchRates, fetchShipping, fetchTexts } from '../../../airtable/settings';
import { createSubmissionCardsOptions, deleteSubmission, fetchSubmissions, fetchSubmissionsCards, updateSubmissionTracking } from '../../../airtable/submissions';
import { computeSubmissionCart, createSubmissionCart } from '../../../business/submission-cart';
import { createCheckoutSession } from '../../../stripe';
import { showAnswersDialog } from '../../components/confirmation-dialog-service';
import { LoadingOverlay } from '../../components/loading-overlay';

import { clearDraft, hasDraft, storeDraftForm, storeDraftTable } from './commons/memory';
import { useStyles } from './commons/styles';
import { getSubmissionCards } from './commons/utils';
import { ConfirmedSubmissionsTable } from './components/confirmed-submissions-table';
import { PendingSubmissionsTable } from './components/pending-submissions-table';
import { DRAFT_SUBMISSION_ROUTE } from './routes';

export function SubmissionsPage() {
  const classes = useStyles();
  const history = useHistory();
  const user = useSelector(({ auth }) => auth.user, shallowEqual);
  const subheader = useSubheader();
  subheader.setTitle('Submissions');

  const [allSubmissions, setAllSubmissions] = useState([]);
  const [allSubmissionsCards, setAllSubmissionsCards] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [textBelowSubmit, setTextBelowSubmit] = useState(null);

  useEffect(() => {
    async function fetchAll() {
      const texts = await fetchTexts();
      setTextBelowSubmit(texts.get('submissions-below-submit'));
    }
    void fetchAll();
  }, []);

  useEffect(() => {
    async function fetchAll() {
      setRefreshing(true);

      try {
        const submissions = await fetchSubmissions(user);
        const cards = await fetchSubmissionsCards(submissions);

        setAllSubmissions(computeSubmissions(submissions, cards, {
          coupons: await fetchCoupons(),
          rates: await fetchRates(),
          shipping: await fetchShipping(),
        }));

        setAllSubmissionsCards(cards);
      } finally {
        setRefreshing(false);
      }
    }

    if (refresh) {
      setRefresh(false);
      void fetchAll();
    }
  }, [refresh, user]);

  async function checkoutWithStripe(submission) {
    const session = await createCheckoutSession({
      target: submission.id,
      type: 'submission',
      user,
    });

    if (session.url) {
      window.location.href = session.url;
      return true;
    }

    return false;
  }

  async function checkoutPendingSubmission(submission) {
    setSubmitting(true);

    try {
      await checkoutWithStripe(submission);
    } catch (error) {
      setStatus({ error: true, text: `${error.message}: ${error.response.data.message}` });
      setSubmitting(false);
    }
  }

  function isLoading() {
    return refreshing || submitting;
  }

  function renderActionsCard() {
    const buttonText = hasDraft()
      ? 'Resume submission draft'
      : 'Create new submission';

    return (
      <div className={classes.card} style={{ flexDirection: 'row' }}>
        <Button
          onClick={event => {
            event.stopPropagation();
            setSubmitting(true);
            history.push(DRAFT_SUBMISSION_ROUTE);
          }}
          style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            marginRight: '1rem',
          }}
        >
          <span>{buttonText}</span>
        </Button>
      </div>
    );
  }

  function confirmEdit(submission) {
    const submissionNumber = submission.fields[submissionFields.SUBMISSION_NUMBER];
    const content = hasDraft()
      ? `You have an unfinished submission draft.

By editing, your current submission draft will be discarded and submission #${submissionNumber} will be converted to a draft, so it will no longer be pending.

Do you want to discard your current submission draft and edit submission #${submissionNumber}?

Your current submission draft will be lost.`
      : `Edit submission #${submissionNumber}?`;

    const confirmText = hasDraft()
      ? `Yes, discard draft and edit submission #${submissionNumber}`
      : `Yes, edit submission #${submissionNumber}`;

    return showAnswersDialog({
      content: content,
      title: 'ATTENTION',
      renderActions: answer => {
        return (
          <>
            <IconButton
              onClick={async event => {
                event.stopPropagation();
                answer(true);
              }}
            >
              <Icon>delete_outline</Icon>
              <Typography>{confirmText}</Typography>
            </IconButton>

            <IconButton
              onClick={async event => {
                event.stopPropagation();
                answer(false);
              }}
            >
              <Icon>clear</Icon>
              <Typography>No, cancel</Typography>
            </IconButton>
          </>
        );
      },
    });
  }

  function groupSimilarSubmissionCards(submissionCards) {
    const cards = new Map();
    const keys = createSubmissionCardsOptions().managedFields;

    submissionCards = _.map(submissionCards, 'fields');

    for (const submissionsCard of submissionCards) {
      const key = JSON.stringify(_.pick(submissionsCard, keys));
      const card = {
        ...submissionsCard,
        ...cards.get(key),
      };

      card.dciReview = card[submissionCardsFields.DESTINATION] === 'DCI';
      card.quantity = (card.quantity ?? 0) + 1;

      cards.set(key, card);
    }

    return Array.from(cards.values());
  }

  async function executeEditSubmissionAction(submission) {
    setSubmitting(true);

    try {
      const submissionCards = groupSimilarSubmissionCards(getSubmissionCards(submission, allSubmissionsCards));
      console.log('edit', { submission, submissionCards });

      clearDraft();
      storeDraftForm(submission);
      storeDraftTable(submissionCards);
      await deleteSubmission(submission);

      history.push(DRAFT_SUBMISSION_ROUTE);
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete(submission) {
    return showAnswersDialog({
      content: `Delete submission #${submission.fields[submissionFields.SUBMISSION_NUMBER]}?`,
      title: 'ATTENTION',
      renderActions: answer => {
        return (
          <>
            <IconButton
              onClick={async event => {
                event.stopPropagation();
                answer(true);
              }}
            >
              <Icon>delete_outline</Icon>
              <Typography>Yes, delete submission #{submission.fields[submissionFields.SUBMISSION_NUMBER]}</Typography>
            </IconButton>

            <IconButton
              onClick={async event => {
                event.stopPropagation();
                answer(false);
              }}
            >
              <Icon>clear</Icon>
              <Typography>No, cancel</Typography>
            </IconButton>
          </>
        );
      },
    });
  }

  async function executeDeleteSubmissionAction(submission) {
    setSubmitting(true);

    try {
      console.log('delete', { submission });
      await deleteSubmission(submission);
    } finally {
      setRefresh(true);
      setSubmitting(false);
    }
  }

  function renderPendingSubmissionsCard() {
    const renderActions = submission => {
      return <>
        <IconButton
          onClick={async event => {
            event.stopPropagation();

            if (await confirmEdit(submission)) {
              await executeEditSubmissionAction(submission);
            }
          }}
        >
          <Icon>edit</Icon>
        </IconButton>

        <IconButton
          onClick={async event => {
            event.stopPropagation();

            if (await confirmDelete(submission)) {
              await executeDeleteSubmissionAction(submission);
            }
          }}
        >
          <Icon>delete_outline</Icon>
        </IconButton>

        <Button
          className={classes.submitButton}
          disabled={isLoading()}
          onClick={event => {
            void checkoutPendingSubmission(submission);
            event.stopPropagation();
          }}
          type="button"
          variant="primary"
        >
          <span>Checkout</span>
          {isLoading() && <span className="ml-3 spinner spinner-white"/>}
        </Button>
      </>;
    };

    return (
      <Card className={classes.card}>
        <LoadingOverlay active={isLoading()}>
          <PendingSubmissionsTable
            allSubmissions={allSubmissions}
            allSubmissionsCards={allSubmissionsCards}
            renderActions={renderActions}
          />
        </LoadingOverlay>
      </Card>
    );
  }

  function renderConfirmedSubmissionsCard() {
    return (
      <Card className={classes.card}>
        <LoadingOverlay active={isLoading()}>
          <ConfirmedSubmissionsTable
            allSubmissions={allSubmissions}
            allSubmissionsCards={allSubmissionsCards}
            loading={isLoading()}
            onUpdateTracking={async (submission, tracking) => {
              console.log('onUpdateTracking', { submission, tracking });
              await updateSubmissionTracking(submission, tracking);
              setRefresh(true);
            }}
          />
        </LoadingOverlay>
      </Card>
    );
  }

  function renderTextBelowSubmitCard() {
    if (!textBelowSubmit) {
      return null;
    }

    return (
      <Card className={classes.card} style={{ padding: '1rem', whiteSpace: 'pre-line' }}>
        {textBelowSubmit}
      </Card>
    );
  }

  return (
    <div className={classes.root}>
      {status && (
        <div className={`alert alert-custom alert-dismissible alert-light-${status.error ? 'danger' : 'success'} ${classes.status}`}>
          <div className="alert-text font-weight-bold" style={{ whiteSpace: 'pre-line' }}>
            {status.text}
          </div>
        </div>
      )}

      {renderActionsCard()}
      {renderPendingSubmissionsCard()}
      {renderConfirmedSubmissionsCard()}
      {renderTextBelowSubmitCard()}

    </div>
  );
}

function computeSubmissions(submissions, cards, options) {
  return submissions.map(submission => {
    const submissionCards = getSubmissionCards(submission, cards);
    const cart = createSubmissionCart(submission.fields, submissionCards.map(card => ({ ...card.fields })));
    const computedCart = computeSubmissionCart(cart, options);

    return { ...submission, total: computedCart.total };
  });
}
